import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  InputNumber,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined } from '@ant-design/icons';
import { useLiveopsChange } from '@/hooks/useLiveopsChange';
import { adminApi } from '@/services/api';
import { apiErrorMessage } from '@/utils/apiError';
import { CalendarSettingsCard } from '@/pages/cricket-calendar/CalendarSettingsCard';
import { countryFlag, countryName, istDateTime } from '@/utils/cricketCalendar';
import { LANGUAGE_LABEL, MOMENT_LABEL, PLAN_MOMENTS } from '@/utils/matchPushCopy';
import {
  SETTINGS_RANGE,
  settingsChanges,
  settingsFormValues,
  superAdminChanges,
} from '@/utils/matchPushSettings';
import type { SettingsForm } from '@/utils/matchPushSettings';
import type {
  CricketCalendarSettings,
  MatchPushSettings,
  MatchPushStatus,
  PlayerLocaleCoverage,
} from '@/types/matchPushes';

const { Text } = Typography;
const num = (n: number): string => n.toLocaleString('en-IN');

const LANGUAGE_MODES = [
  {
    value: 'experiment',
    label: 'Test: split Hindi-family players into English, Hinglish and Hindi',
  },
  { value: 'en', label: `All ${LANGUAGE_LABEL.en}` },
  { value: 'hinglish', label: `Hindi-family players get ${LANGUAGE_LABEL.hinglish}` },
  { value: 'hi', label: `Hindi-family players get ${LANGUAGE_LABEL.hi}` },
];

/** The sending switch, caps and window, moments, language mode, tracking and locale coverage. */
export const SettingsTab: React.FC = () => {
  const [form] = Form.useForm<SettingsForm>();
  const [status, setStatus] = useState<MatchPushStatus | null>(null);
  const [settings, setSettings] = useState<MatchPushSettings | null>(null);
  const [calendarSettings, setCalendarSettings] = useState<CricketCalendarSettings | null>(null);
  const [coverage, setCoverage] = useState<PlayerLocaleCoverage | null>(null);
  const [loading, setLoading] = useState(false);
  const [switching, setSwitching] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // What the form holds now, to tell which changes need a super admin.
  const [draft, setDraft] = useState<SettingsForm | null>(null);
  const { canChange, deniedTitle } = useLiveopsChange();

  const showSettings = useCallback(
    (next: MatchPushSettings) => {
      const values = settingsFormValues(next);
      setSettings(next);
      form.setFieldsValue(values);
      setDraft(values);
    },
    [form]
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [s, st, c, cov] = await Promise.allSettled([
        adminApi.matchPushes.getSettings(),
        adminApi.matchPushes.getStatus(),
        adminApi.cricketCalendar.getSettings(),
        adminApi.playerLocale.coverage(),
      ]);
      if (s.status === 'fulfilled') showSettings(s.value.data);
      else setError(apiErrorMessage(s.reason, 'Failed to load the settings'));
      if (st.status === 'fulfilled') setStatus(st.value.data);
      if (c.status === 'fulfilled') setCalendarSettings(c.value.data);
      if (cov.status === 'fulfilled') setCoverage(cov.value.data);
    } finally {
      setLoading(false);
    }
  }, [showSettings]);

  useEffect(() => {
    load();
  }, [load]);

  const applySwitch = async (on: boolean) => {
    setSwitching(true);
    try {
      const response = await adminApi.matchPushes.setSwitch(on);
      setStatus(response.data);
      message.success(on ? 'Automatic sending is on' : 'Automatic sending is off');
    } catch (e) {
      message.error(apiErrorMessage(e, 'Failed to change the switch'));
    } finally {
      setSwitching(false);
    }
  };

  // Changes in the form that only a super admin may save (shown to the others before they press Save).
  const loaded = settings ? settingsFormValues(settings) : null;
  const needsSuperAdmin =
    loaded && draft && status && !status.canSwitchOn
      ? superAdminChanges(loaded, settingsChanges(loaded, draft))
      : [];

  const onSwitch = (on: boolean) => {
    if (!on) {
      applySwitch(false);
      return;
    }
    const hours = settings
      ? `${String(settings.windowStartHour).padStart(2, '0')}:00 to ${String(settings.windowEndHour).padStart(2, '0')}:00 IST`
      : 'the send window';
    Modal.confirm({
      title: 'Switch automatic match pushes on?',
      content: `From now on the queue sends by itself, every minute, ${hours}, up to ${
        settings?.dailyCapPerPlayer ?? 'the daily cap'
      } match pushes per player a day. Pause or cancel single pushes from the Upcoming tab.`,
      okText: 'Switch on',
      onOk: () => applySwitch(true),
    });
  };

  const save = async () => {
    let values: SettingsForm;
    try {
      values = await form.validateFields();
    } catch {
      return; // the form shows which fields to fix
    }
    if (values.windowStartHour >= values.windowEndHour) {
      message.error('The window must end after it starts');
      return;
    }
    if (!settings) return;
    // Only what this admin changed: the server patches field by field and checks the super admin rule on what is sent.
    const body = settingsChanges(settingsFormValues(settings), values);
    if (Object.keys(body).length === 0) {
      message.info('Nothing changed');
      return;
    }
    setSaving(true);
    try {
      const response = await adminApi.matchPushes.updateSettings(body);
      showSettings(response.data);
      message.success('Match push settings saved');
    } catch (e) {
      message.error(apiErrorMessage(e, 'Failed to save the match push settings'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Space direction="vertical" size={16} style={{ width: '100%' }}>
      {error && <Alert type="error" showIcon message={error} />}
      <Card
        title="Automatic sending"
        loading={loading && !status}
        extra={
          <Button icon={<ReloadOutlined />} onClick={load} loading={loading}>
            Refresh
          </Button>
        }
      >
        {status && (
          <Row gutter={[24, 12]}>
            <Col xs={24} md={8}>
              <Space direction="vertical" size={4}>
                <Space>
                  <Tooltip title={deniedTitle}>
                    <Switch
                      checked={status.on}
                      loading={switching}
                      disabled={!canChange || (!status.on && !status.canSwitchOn)}
                      onChange={onSwitch}
                    />
                  </Tooltip>
                  <Text strong>{status.on ? 'On' : 'Off'}</Text>
                </Space>
                {!status.on && !status.canSwitchOn && (
                  <Text type="secondary">Only super admins can switch on.</Text>
                )}
              </Space>
            </Col>
            <Col xs={24} md={16}>
              <Space direction="vertical" size={2}>
                <Text>
                  Sent today <b>{num(status.sentToday)}</b> · held out (no push){' '}
                  <b>{num(status.heldOutToday)}</b>
                </Text>
                <Text type="secondary">
                  {status.nextPlan
                    ? `Next: ${MOMENT_LABEL[status.nextPlan.moment]}, ${status.nextPlan.fixtureLabel}${
                        status.nextPlan.at ? `, ${istDateTime(status.nextPlan.at)}` : ''
                      }`
                    : 'No push waiting.'}
                </Text>
              </Space>
            </Col>
          </Row>
        )}
      </Card>

      <Card title="Limits, window and copy" loading={loading && !settings}>
        <Form
          form={form}
          layout="vertical"
          disabled={!canChange}
          onValuesChange={(_, all) => setDraft(all as SettingsForm)}
        >
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item
                name="dailyCapPerPlayer"
                label="Daily cap per player"
                extra="Match pushes a day; the lifecycle push is separate."
                rules={[{ required: true, type: 'number', ...SETTINGS_RANGE.dailyCapPerPlayer }]}
              >
                <InputNumber
                  {...SETTINGS_RANGE.dailyCapPerPlayer}
                  precision={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="windowStartHour"
                label="Window starts (IST hour)"
                rules={[{ required: true, type: 'number', ...SETTINGS_RANGE.windowStartHour }]}
              >
                <InputNumber
                  {...SETTINGS_RANGE.windowStartHour}
                  precision={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="windowEndHour"
                label="Window ends (IST hour)"
                rules={[{ required: true, type: 'number', ...SETTINGS_RANGE.windowEndHour }]}
              >
                <InputNumber
                  {...SETTINGS_RANGE.windowEndHour}
                  precision={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="holdoutPercent"
                label="Holdout (get no push) %"
                rules={[{ required: true, type: 'number', ...SETTINGS_RANGE.holdoutPercent }]}
              >
                <InputNumber
                  {...SETTINGS_RANGE.holdoutPercent}
                  precision={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="maxInactiveDays"
                label="Skip players inactive for (days)"
                rules={[{ required: true, type: 'number', ...SETTINGS_RANGE.maxInactiveDays }]}
              >
                <InputNumber
                  {...SETTINGS_RANGE.maxInactiveDays}
                  precision={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item
                name="skipActiveMinutes"
                label="Skip players active in the last (min)"
                rules={[{ required: true, type: 'number', ...SETTINGS_RANGE.skipActiveMinutes }]}
              >
                <InputNumber
                  {...SETTINGS_RANGE.skipActiveMinutes}
                  precision={0}
                  style={{ width: '100%' }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="languageMode" label="Language for Hindi-family players">
                <Select options={LANGUAGE_MODES} />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item label="Moments that get a push">
            <Space size={[24, 8]} wrap>
              {PLAN_MOMENTS.map((moment) => (
                <Form.Item key={moment} name={['moments', moment]} valuePropName="checked" noStyle>
                  <MomentSwitch label={MOMENT_LABEL[moment]} />
                </Form.Item>
              ))}
            </Space>
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Switching a moment off cancels its unsent pushes.
              </Text>
            </div>
          </Form.Item>
          {needsSuperAdmin.length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message={`Only a super admin can ${needsSuperAdmin.join(' or ')}: it makes more pushes go out.`}
            />
          )}
          <Tooltip title={deniedTitle}>
            <Button type="primary" loading={saving} disabled={!canChange} onClick={save}>
              Save match push settings
            </Button>
          </Tooltip>
          {settings?.updatedBy && (
            <Text type="secondary" style={{ marginLeft: 12 }}>
              Last saved by {settings.updatedBy}, {istDateTime(settings.updatedAt)}
            </Text>
          )}
          {status && !status.canSwitchOn && (
            <div>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Raising the cap, widening the window, lowering the holdout, raising the inactive
                days, lowering the active-player skip or switching a moment on needs a super admin.
              </Text>
            </div>
          )}
        </Form>
      </Card>

      <CalendarSettingsCard settings={calendarSettings} onSaved={setCalendarSettings} />

      <Card title="Locale coverage" size="small" loading={loading && !coverage}>
        {coverage && (
          <Space direction="vertical" size={8} style={{ width: '100%' }}>
            <Text>
              {num(coverage.total)} players have a country · {num(coverage.hindiFamily)} are
              Hindi-family
              {coverage.lastSyncAt ? ` · synced ${istDateTime(coverage.lastSyncAt)}` : ''}
            </Text>
            {coverage.note && <Alert type="info" showIcon message={coverage.note} />}
            <Row gutter={16}>
              <Col xs={24} md={14}>
                <Table
                  size="small"
                  rowKey={(r, index) =>
                    `${r.countryCode ?? 'none'}-${r.countryName ?? ''}-${index}`
                  }
                  pagination={false}
                  dataSource={coverage.byCountry}
                  columns={[
                    {
                      title: 'Country',
                      key: 'country',
                      render: (_: unknown, r: PlayerLocaleCoverage['byCountry'][number]) =>
                        r.countryCode
                          ? `${countryFlag(r.countryCode)} ${r.countryName ?? countryName(r.countryCode)}`
                          : (r.countryName ?? '(not set)'),
                    },
                    {
                      title: 'Players',
                      dataIndex: 'players',
                      align: 'right' as const,
                      render: num,
                    },
                  ]}
                />
                {coverage.byCountry.length >= 20 && (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    Top 20 countries by players.
                  </Text>
                )}
              </Col>
              <Col xs={24} md={10}>
                <Table
                  size="small"
                  rowKey="arm"
                  pagination={false}
                  dataSource={coverage.byLanguageArm}
                  columns={[
                    {
                      title: 'Language arm',
                      dataIndex: 'arm',
                      render: (arm: keyof typeof LANGUAGE_LABEL) => LANGUAGE_LABEL[arm] ?? arm,
                    },
                    {
                      title: 'Players',
                      dataIndex: 'players',
                      align: 'right' as const,
                      render: num,
                    },
                  ]}
                />
              </Col>
            </Row>
          </Space>
        )}
      </Card>
    </Space>
  );
};

/** A labelled switch usable as a Form.Item child (`checked` / `onChange`). */
const MomentSwitch: React.FC<{
  label: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
}> = ({ label, checked, onChange }) => (
  <Space>
    <Switch checked={checked} onChange={onChange} />
    <Text>{label}</Text>
  </Space>
);
